<template>
    <template v-if="activeFacilities.length > 1">
        <popup-menu>
            <template #default="{ anchorProps }">
                <prime-button
                    v-bind="anchorProps"
                    type="button"
                    text
                    severity="secondary"
                    icon="pi pi-angle-down"
                    icon-pos="right"
                    :label="t('layout.ourLocations')"
                    :pt="{
                        label: '!font-medium'
                    }"
                    :pt-options="{ mergeProps: true }"
                />
            </template>

            <template #content>
                <div
                    v-if="useGroupedLayout"
                    class="grid gap-6 p-2"
                    :class="columnClass"
                >
                    <div
                        v-for="(column, columnIndex) in groupColumns"
                        :key="`column-${columnIndex}`"
                        class="space-y-4"
                    >
                        <div
                            v-for="group in column"
                            :key="group.id"
                            class="space-y-2 border-b-2 border-gray-300 pb-3 last:border-b-0 last:pb-0"
                        >
                            <div
                                class="text-base font-semibold uppercase tracking-wide text-gray-800"
                            >
                                <span class="inline-flex items-center gap-2">
                                    <span
                                        class="h-2 w-2 rounded-full bg-primary-600"
                                    />
                                    {{ group.name }}
                                </span>
                            </div>

                            <popup-menu-link
                                v-for="facility in group.facilities"
                                :key="facility.label"
                                :label="facility.label"
                                :to="facility.route"
                            />
                        </div>
                    </div>
                </div>
                <div v-else>
                    <popup-menu-link
                        v-for="facility in activeFacilities"
                        :key="facility.label"
                        :label="facility.label"
                        :to="facility.route"
                    />
                </div>
            </template>
        </popup-menu>
    </template>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import { useRpQuery } from '~/composables/graphql'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { computed } from 'vue'
import { useRouteLink } from '~/composables/routing'
import PopupMenu from '~/components/PopupMenu/PopupMenu.vue'
import PopupMenuLink from '~/components/PopupMenu/PopupMenuLink.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })
const query = useRpQuery({ orgLevel: true })
// TODO: when there are more than 30 facilities
// TODO: Limit to active facilities on server
const { data, suspense } = useQuery({
    queryKey: ['OurLocationsQuery'],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query OurLocationsQuery {
                    organization {
                        facilityLocationGroups {
                            id
                            name
                            facilities {
                                id
                                label
                                slug
                            }
                        }
                    }
                }
            `)
        )
})

const { facilityHomeLink } = useRouteLink()

type FacilityLink = {
    id: string
    label: string
    route: string
}

type FacilityGroupDisplay = {
    id: string
    name: string
    facilities: FacilityLink[]
}

const groupedFacilities = computed<FacilityGroupDisplay[]>(() => {
    return (
        data.value?.organization.facilityLocationGroups.map(group => ({
            id: group.id,
            name: group.name,
            facilities: group.facilities.map(facility => ({
                id: facility.id,
                label: facility.label,
                route: facilityHomeLink(facility.slug.toLowerCase())
            }))
        })) ?? []
    )
})

const activeFacilities = computed<FacilityLink[]>(() => {
    return groupedFacilities.value.flatMap(group => group.facilities)
})

const useGroupedLayout = computed(() => groupedFacilities.value.length > 1)

const groupColumns = computed<FacilityGroupDisplay[][]>(() => {
    const groups = groupedFacilities.value
    if (groups.length === 0) {
        return []
    }

    const sizes = groups.map(group => 1 + group.facilities.length)
    const prefix = sizes.reduce<number[]>((acc, size) => {
        acc.push((acc.at(-1) ?? 0) + size)
        return acc
    }, [])

    const sumRange = (start: number, end: number) =>
        prefix[end - 1] - (start > 0 ? prefix[start - 1] : 0)

    type Partition = {
        columnCount: number
        splits: number[]
        maxSum: number
        variance: number
    }

    const partitions: Partition[] = []

    const total = prefix[prefix.length - 1]

    const recordPartition = (columnCount: number, splits: number[]) => {
        const boundaries = [0, ...splits, groups.length]
        const sums = boundaries
            .slice(1)
            .map((end, index) => sumRange(boundaries[index], end))
        const maxSum = Math.max(...sums)
        const mean = total / columnCount
        const variance =
            sums.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) /
            columnCount
        partitions.push({ columnCount, splits, maxSum, variance })
    }

    // 1 column
    recordPartition(1, [])

    // 2 columns
    if (groups.length >= 2) {
        for (let i = 1; i < groups.length; i += 1) {
            recordPartition(2, [i])
        }
    }

    // 3 columns
    if (groups.length >= 3) {
        for (let i = 1; i < groups.length - 1; i += 1) {
            for (let j = i + 1; j < groups.length; j += 1) {
                recordPartition(3, [i, j])
            }
        }
    }

    // 4 columns
    if (groups.length >= 4) {
        for (let i = 1; i < groups.length - 2; i += 1) {
            for (let j = i + 1; j < groups.length - 1; j += 1) {
                for (let k = j + 1; k < groups.length; k += 1) {
                    recordPartition(4, [i, j, k])
                }
            }
        }
    }

    const best = partitions.sort((a, b) => {
        if (a.maxSum !== b.maxSum) {
            return a.maxSum - b.maxSum
        }
        if (a.variance !== b.variance) {
            return a.variance - b.variance
        }
        return a.columnCount - b.columnCount
    })[0]

    const boundaries = [0, ...best.splits, groups.length]
    return boundaries.slice(1).map((end, index) => {
        return groups.slice(boundaries[index], end)
    })
})

const columnClass = computed(() => {
    const count = groupColumns.value.length
    if (count >= 4) {
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
    }
    if (count === 3) {
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    }
    if (count === 2) {
        return 'grid-cols-1 md:grid-cols-2'
    }
    return 'grid-cols-1'
})

await suspense()
</script>
